import { createTheme } from "@mui/material/styles";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";

// Classes Types
import { buttonBaseClasses } from "@mui/material/ButtonBase";
import { buttonClasses } from "@mui/material/Button";

// Hooks
import useTailwindDarkMode from "./hooks/darkMode";

const BLUE_COLOR = "rgba(0, 140, 255, 1)";
const PURPLE_COLOR = "#8052ff";
const HAITI_900_COLOR = "#281f48";
const STONE_650_COLOR = "#3B3F42"
const ZEUS_700_COLOR = "#2B2B2B";
const ZEUS_800_COLOR = "#313131";
const HAITI_800_COLOR = "#30264f";
const WHITE_COLOR = "#FFFFFF";
const RED_COLOR = "rgba(217, 45, 32, 1)";
const GRAY_50_COLOR = "#f5f5f5";
const GRAY_100_COLOR = "rgba(221, 225, 235, 1)";
const GRAY_400_COLOR = "#666666";


const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const isDarkMode = useTailwindDarkMode();

  const primaryColor = isDarkMode ? ZEUS_700_COLOR : BLUE_COLOR;
  const secondaryColor = isDarkMode ? ZEUS_800_COLOR : GRAY_50_COLOR;
  const errorColor = RED_COLOR

  const theme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: primaryColor,
        light: WHITE_COLOR,
        dark: primaryColor
      },
      secondary: {
        main: secondaryColor,
      },
      error: {
        main: errorColor,
      }
    },
    components: {
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '24px',
            backgroundColor: isDarkMode ? STONE_650_COLOR : WHITE_COLOR,
            backgroundImage: 'none',
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? STONE_650_COLOR : WHITE_COLOR,
            '& .MuiInputBase-root': {
              backgroundColor: isDarkMode ? ZEUS_800_COLOR : WHITE_COLOR,
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              '&:focus': {
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
              },
              '&:hover': {
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
              }
            }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            '& .MuiSelect-select': {
              backgroundColor: isDarkMode ? ZEUS_800_COLOR : WHITE_COLOR,
            }
          }
        }
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            borderRadius: '99px',
          },
          [`.${buttonClasses.disabled}`]: {
            cursor: 'not-allowed',
          },
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: '99px',
            backgroundColor: isDarkMode ? ZEUS_800_COLOR : GRAY_100_COLOR,
            padding: '4px',
            [`& .${buttonBaseClasses.root}`]: {
              borderRadius: '99px !important',
              border: 'none',
            }
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme: muiTheme }) => ({
            textTransform: 'none',
            borderRadius: '99px',
            gap: muiTheme.spacing(1),

            [`& .${buttonClasses.colorSecondary}`]: {
              boxShadow: 'none',
            },
            '& .MuiButton-startIcon': {
              margin: 0,
            },
            '& .MuiButton-endIcon': {
              margin: 0,
            }
          })
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: '24px',
            backgroundColor: isDarkMode ? STONE_650_COLOR : WHITE_COLOR,
            backgroundImage: 'none',
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: '0px',
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '24px',
            backgroundColor: isDarkMode ? STONE_650_COLOR : WHITE_COLOR,
            backgroundImage: 'none',
          }
        }
      }
    }
  });

  return (
    <MuiThemeProvider
      theme={theme}
    >
      {children}
    </MuiThemeProvider>
  )
};

export default ThemeProvider;
